// 数据同步 - zzshu 镜像表预览屏。
//
// 列出 fuelio-crawler-db.zzshu_commodities 全部行：cover 缩略图 / 名称 /
// 品类 / 价格 / 库存 / 库存状态 / 上次同步。点行展开 modal 看完整 markdown
// 描述 + 大图 + 源页链接，方便人眼校对"爬下来的数据对不对"。
//
// 仅只读。"导入到 fuelio products" 是下一阶段的功能（M3 时再加）。

import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { RefreshCw, ChevronLeft, ExternalLink, X, Download, Unlink2 } from 'lucide-react'
import {
  api, errorMessage,
  type ZzshuCategoryRow, type ZzshuCommodityRow, type ZzshuCommodityFullRow,
} from '@/lib/api'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Input } from '@/components/ui/Input'
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/Table'
import { useToast } from '@/components/ui/Toast'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { PageError, PageLoading } from './Stats'
import { DataSyncImportModal } from './DataSyncImportModal'

function fmtPrice(cents: number): string {
  return '¥' + (cents / 100).toFixed(2).replace(/\.00$/, '')
}

function fmtRelative(unix: number | null): string {
  if (!unix) return '—'
  const diff = Math.floor(Date.now() / 1000) - unix
  if (diff < 60) return `${diff} 秒前`
  if (diff < 3600) return `${Math.floor(diff / 60)} 分钟前`
  if (diff < 86400) return `${Math.floor(diff / 3600)} 小时前`
  return `${Math.floor(diff / 86400)} 天前`
}

function StockBadge({ stock, state }: { stock: number; state: 0 | 1 | 2 }) {
  // 0 = 缺货（红） / 1 = 少量（橙） / 2 = 充足（绿）
  const variants = {
    0: { label: '缺货', cls: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
    1: { label: '少量', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
    2: { label: '充足', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
  } as const
  const v = variants[state] ?? variants[0]
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${v.cls}`}>
      <span className="font-mono">{stock}</span>
      <span className="opacity-80">·</span>
      {v.label}
    </span>
  )
}

export function DataSyncZzshuScreen() {
  const toast = useToast()
  const [cats, setCats] = useState<ZzshuCategoryRow[]>([])
  const [rows, setRows] = useState<ZzshuCommodityRow[]>([])
  const [err, setErr] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  // filters
  const [categoryFilter, setCategoryFilter] = useState<number | null>(null)
  const [stockFilter, setStockFilter] = useState<0 | 1 | 2 | null>(null)
  const [keyword, setKeyword] = useState('')

  // detail modal (查看上游)
  const [detailId, setDetailId] = useState<number | null>(null)
  const [detail, setDetail] = useState<ZzshuCommodityFullRow | null>(null)
  const [detailErr, setDetailErr] = useState<string | null>(null)

  // import modal (导入到 fuelio products)
  const [importId, setImportId] = useState<number | null>(null)

  // per-row "更新" 进行中状态 + 解链确认
  const [resyncing, setResyncing] = useState<Set<number>>(new Set())
  const [unlinkConfirm, setUnlinkConfirm] = useState<{ commodityId: number; sku: string } | null>(null)

  const load = async () => {
    setLoading(true); setErr(null)
    try {
      const [c, r] = await Promise.all([
        api.dataSyncZzshuCategories(),
        api.dataSyncZzshuCommodities({
          ...(categoryFilter !== null ? { category: categoryFilter } : {}),
          ...(stockFilter !== null ? { stockState: stockFilter } : {}),
          ...(keyword ? { q: keyword } : {}),
        }),
      ])
      setCats(c.categories)
      setRows(r.commodities)
    } catch (e) {
      setErr(errorMessage(e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void load() }, [categoryFilter, stockFilter])

  const catNameById = useMemo(() => {
    const m = new Map<number, string>()
    for (const c of cats) m.set(c.id, c.name)
    return m
  }, [cats])

  const openDetail = async (id: number) => {
    setDetailId(id); setDetail(null); setDetailErr(null)
    try {
      const r = await api.dataSyncZzshuCommodity(id)
      setDetail(r.commodity)
    } catch (e) {
      setDetailErr(errorMessage(e))
    }
  }
  const closeDetail = () => { setDetailId(null); setDetail(null); setDetailErr(null) }

  // ── resync：把上游最新 price/stock 推给已链接的 fuelio product ───
  async function resync(commodityId: number) {
    setResyncing((prev) => new Set(prev).add(commodityId))
    try {
      const r = await api.dataSyncZzshuResync(commodityId)
      const bits: string[] = []
      if (r.syncedPrice !== null) bits.push(`价格 ¥${(r.syncedPrice / 100).toFixed(2)}`)
      if (r.syncedStock !== null) bits.push(`库存 ${r.syncedStock}`)
      toast.success(`${r.sku} 已更新${bits.length ? '：' + bits.join(' / ') : ''}`)
      await load()  // 刷表格让 link_synced_at 走最新值
    } catch (e) {
      toast.error(errorMessage(e))
    } finally {
      setResyncing((prev) => { const n = new Set(prev); n.delete(commodityId); return n })
    }
  }

  // ── unlink：只解链，不删 fuelio product ─────────────────────────
  async function unlink() {
    if (!unlinkConfirm) return
    try {
      await api.dataSyncZzshuUnlink(unlinkConfirm.commodityId)
      toast.success(`已解除链接 ${unlinkConfirm.sku}`)
      setUnlinkConfirm(null)
      await load()
    } catch (e) {
      toast.error(errorMessage(e))
    }
  }

  if (loading && rows.length === 0) return <PageLoading/>
  if (err && rows.length === 0) return <PageError msg={err}/>

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Link to="/data-sync" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
            <ChevronLeft className="w-4 h-4"/>返回数据同步
          </Link>
        </div>
        <Button variant="outline" size="sm" onClick={() => void load()}>
          <RefreshCw className="w-4 h-4 mr-2"/>刷新
        </Button>
      </header>

      <div>
        <h1 className="text-2xl font-semibold">吱吱鼠卡网（zzshu.com）</h1>
        <p className="text-sm text-muted-foreground mt-1">
          上游 SKU 镜像。当前 <span className="font-mono">{rows.length}</span> 个商品 · <span className="font-mono">{cats.length}</span> 个品类。
          重跑导入：<code className="text-xs bg-muted px-1.5 py-0.5 rounded">cd crawlers/zzshu && pnpm import:local</code>
        </p>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">品类:</span>
            <button
              className={`px-3 py-1 rounded text-xs ${categoryFilter === null ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'}`}
              onClick={() => setCategoryFilter(null)}
            >全部</button>
            {cats.map((c) => (
              <button
                key={c.id}
                className={`px-3 py-1 rounded text-xs ${categoryFilter === c.id ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'}`}
                onClick={() => setCategoryFilter(c.id)}
              >{c.name} <span className="opacity-70">({c.commodity_count})</span></button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">库存:</span>
            {([
              [null, '全部'], [2, '充足'], [1, '少量'], [0, '缺货'],
            ] as const).map(([v, label]) => (
              <button
                key={String(v)}
                className={`px-3 py-1 rounded text-xs ${stockFilter === v ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'}`}
                onClick={() => setStockFilter(v as any)}
              >{label}</button>
            ))}
          </div>
          <form className="flex items-center gap-2 ml-auto" onSubmit={(e) => { e.preventDefault(); void load() }}>
            <Input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索商品名…"
              className="w-48"
            />
            <Button variant="outline" size="sm" type="submit">搜索</Button>
          </form>
        </div>
      </Card>

      {/* Table */}
      <Card className="p-0 overflow-hidden">
        <Table>
          <THead>
            <Tr>
              <Th className="w-20">主图</Th>
              <Th>名称</Th>
              <Th className="w-24">品类</Th>
              <Th className="w-28 text-right">价格</Th>
              <Th className="w-28">库存</Th>
              <Th className="w-32">fuelio 链接</Th>
              <Th className="w-32 text-right">同步</Th>
              <Th className="w-56 text-right">操作</Th>
            </Tr>
          </THead>
          <TBody>
            {rows.map((r) => {
              const linked = !!r.fuelio_sku
              return (
                <Tr key={r.id} className="hover:bg-muted/40">
                  <Td>
                    {r.cover ? (
                      <img src={r.cover} alt="" loading="lazy"
                        className="w-14 h-14 object-cover rounded border border-border bg-muted"/>
                    ) : (
                      <div className="w-14 h-14 rounded bg-muted flex items-center justify-center text-muted-foreground text-xs">无</div>
                    )}
                  </Td>
                  <Td>
                    <div className="font-medium leading-tight line-clamp-2 max-w-xl">{r.name}</div>
                    <div className="text-xs text-muted-foreground font-mono mt-1">id = {r.id} · 销量 {r.order_sold}</div>
                  </Td>
                  <Td>
                    <Badge>{catNameById.get(r.category_id ?? -1) ?? '—'}</Badge>
                  </Td>
                  <Td className="text-right font-mono">{fmtPrice(r.price_cents)}</Td>
                  <Td><StockBadge stock={r.stock} state={r.stock_state}/></Td>
                  <Td>
                    {linked ? (
                      <div className="text-xs">
                        <span className="inline-block px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 font-mono">
                          ✓ {r.fuelio_sku}
                        </span>
                        {r.link_synced_at && (
                          <div className="text-muted-foreground mt-1">{fmtRelative(r.link_synced_at)} 已更新</div>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">未导入</span>
                    )}
                  </Td>
                  <Td className="text-right text-xs text-muted-foreground">{fmtRelative(r.synced_at)}</Td>
                  <Td className="text-right space-x-1">
                    <Button size="sm" variant="outline" onClick={() => void openDetail(r.id)}>查看</Button>
                    {linked ? (
                      <>
                        <Button
                          size="sm" variant="outline"
                          disabled={resyncing.has(r.id)}
                          onClick={() => void resync(r.id)}
                          title="把上游最新 价格/库存 同步给已链接的 fuelio product"
                        >
                          <RefreshCw className={`w-3.5 h-3.5 ${resyncing.has(r.id) ? 'animate-spin' : ''}`}/>
                        </Button>
                        <Button
                          size="sm" variant="outline"
                          onClick={() => setUnlinkConfirm({ commodityId: r.id, sku: r.fuelio_sku! })}
                          title="解除映射（不删 fuelio product）"
                        >
                          <Unlink2 className="w-3.5 h-3.5"/>
                        </Button>
                      </>
                    ) : (
                      <Button size="sm" onClick={() => setImportId(r.id)}>
                        <Download className="w-3.5 h-3.5 mr-1"/>导入
                      </Button>
                    )}
                  </Td>
                </Tr>
              )
            })}
          </TBody>
        </Table>
        {rows.length === 0 && !loading && (
          <div className="p-8 text-center text-muted-foreground text-sm">
            没有数据。请先在 crawlers/zzshu 目录跑 <code>pnpm import:local</code> 导入。
          </div>
        )}
      </Card>

      {/* Import modal —— 把 zzshu 行复制成 fuelio products */}
      {importId !== null && (
        <DataSyncImportModal
          commodityId={importId}
          onClose={() => setImportId(null)}
          onSuccess={() => { setImportId(null); void load() }}
        />
      )}

      {/* Unlink 确认 */}
      <ConfirmDialog
        open={!!unlinkConfirm}
        title="解除链接"
        message={unlinkConfirm ? `确定解除 ${unlinkConfirm.sku} 与上游的映射关系？fuelio 主库的商品不会被删，但以后"更新"按钮就找不到对应上游了。` : ''}
        confirmLabel="解除"
        cancelLabel="取消"
        tone="danger"
        onConfirm={() => void unlink()}
        onCancel={() => setUnlinkConfirm(null)}
      />

      {/* Detail modal */}
      {detailId !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto"
          onClick={closeDetail}
        >
          <div
            className="bg-card border border-border rounded-lg max-w-4xl w-full my-8 max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-border">
              <h3 className="font-semibold">商品详情 · zzshu#{detailId}</h3>
              <button onClick={closeDetail} className="text-muted-foreground hover:text-foreground">
                <X className="w-5 h-5"/>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {detailErr && <PageError msg={detailErr}/>}
              {!detail && !detailErr && <PageLoading/>}
              {detail && (
                <>
                  <div className="flex items-start gap-4">
                    {detail.cover && (
                      <a href={detail.cover} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                        <img src={detail.cover} alt="" className="w-40 h-40 object-cover rounded border border-border"/>
                      </a>
                    )}
                    <div className="flex-1 space-y-2 text-sm">
                      <div className="font-medium leading-snug">{detail.name}</div>
                      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
                        <div><span className="text-muted-foreground">价格</span> <span className="font-mono">{fmtPrice(detail.price_cents)}</span></div>
                        <div><span className="text-muted-foreground">会员价</span> <span className="font-mono">{fmtPrice(detail.user_price_cents)}</span></div>
                        <div><span className="text-muted-foreground">库存</span> <StockBadge stock={detail.stock} state={detail.stock_state}/></div>
                        <div><span className="text-muted-foreground">已售</span> <span className="font-mono">{detail.order_sold}</span></div>
                        <div><span className="text-muted-foreground">品类</span> {catNameById.get(detail.category_id ?? -1) ?? '—'}</div>
                        <div><span className="text-muted-foreground">同步</span> {fmtRelative(detail.synced_at)}</div>
                      </div>
                      {detail.source_url && (
                        <a
                          href={detail.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                        >
                          上游页面 <ExternalLink className="w-3 h-3"/>
                        </a>
                      )}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs text-muted-foreground mb-2">宝贝详情（HTML 已自动转 markdown）</div>
                    {detail.description_md ? (
                      <pre className="bg-muted/40 border border-border rounded p-3 text-xs leading-relaxed whitespace-pre-wrap font-mono overflow-x-auto max-h-[400px]">
                        {detail.description_md}
                      </pre>
                    ) : (
                      <div className="text-xs text-muted-foreground italic">无描述</div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
